import React, { useState, useRef } from 'react';
import { COLORS, SPACING } from '../styles/theme';
import { publishEventListing } from '../lib/eventListings';
import { seedFile } from '../lib/torrent';

interface Props {
  onPublished?: () => void;
  defaultOpen?: boolean;
}

export function EventComposer({ onPublished, defaultOpen = false }: Props) {
  const [open, setOpen] = useState(defaultOpen);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [venue, setVenue] = useState('');
  const [dateStr, setDateStr] = useState('');
  const [timeStr, setTimeStr] = useState('');
  const [endTimeStr, setEndTimeStr] = useState('');
  const [tagsRaw, setTagsRaw] = useState('');
  const [flyerMagnetURI, setFlyerMagnetURI] = useState<string | undefined>();
  const [flyerType, setFlyerType] = useState<'image' | 'video' | undefined>();
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileRef = useRef<HTMLInputElement>(null);

  const inputStyle: React.CSSProperties = {
    width: '100%', background: COLORS.panel, color: COLORS.text,
    border: `1px solid ${COLORS.border}`, padding: SPACING.sm,
    fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px',
    outline: 'none', boxSizing: 'border-box',
  };

  const handleFlyerAttach = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const type = file.type.startsWith('image/') ? 'image'
      : file.type.startsWith('video/') ? 'video'
      : null;
    if (!type) { setError('Flyer must be an image or video'); return; }
    setError(null);
    try {
      const result = await seedFile(file);
      setFlyerMagnetURI(result.magnetURI);
      setFlyerType(type as 'image' | 'video');
    } catch {
      setError('Failed to seed flyer');
    }
    e.target.value = '';
  };

  const handleSubmit = async () => {
    if (!title.trim() || !dateStr || !timeStr) {
      setError('Title, date, and time are required');
      return;
    }
    const startTime = new Date(`${dateStr}T${timeStr}`).getTime();
    if (isNaN(startTime)) { setError('Invalid date or time'); return; }
    const endTime = endTimeStr ? new Date(`${dateStr}T${endTimeStr}`).getTime() : undefined;
    const tags = tagsRaw.split(',').map(t => t.trim().toLowerCase()).filter(Boolean);

    setPosting(true);
    setError(null);
    try {
      await publishEventListing(title.trim(), description.trim(), startTime, {
        endTime,
        venue: venue.trim() || undefined,
        tags: tags.length ? tags : undefined,
        flyerMagnetURI,
        flyerType,
      });
      setTitle(''); setDescription(''); setVenue('');
      setDateStr(''); setTimeStr(''); setEndTimeStr('');
      setTagsRaw(''); setFlyerMagnetURI(undefined); setFlyerType(undefined);
      setOpen(false);
      onPublished?.();
    } catch {
      setError('Failed to publish event');
    } finally {
      setPosting(false);
    }
  };

  if (!open) {
    return (
      <div style={{ padding: SPACING.md, borderBottom: `1px solid ${COLORS.border}` }}>
        <button
          onClick={() => setOpen(true)}
          style={{
            width: '100%', background: 'none', border: `1px solid ${COLORS.border}`,
            color: COLORS.textMuted, padding: SPACING.md, cursor: 'pointer',
            fontFamily: '"IBM Plex Mono", monospace', fontSize: '13px', textAlign: 'left',
          }}
        >
          + list an event
        </button>
      </div>
    );
  }

  return (
    <div style={{ padding: SPACING.lg, borderBottom: `2px solid ${COLORS.border}` }}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: SPACING.sm }}>
        <input type="text" value={title} onChange={e => setTitle(e.target.value)} placeholder="event name *" style={inputStyle} />
        <textarea
          value={description} onChange={e => setDescription(e.target.value)}
          placeholder="what is it?" rows={3}
          style={{ ...inputStyle, resize: 'vertical' }}
        />
        <input type="text" value={venue} onChange={e => setVenue(e.target.value)} placeholder="venue name (optional)" style={inputStyle} />
        <div style={{ display: 'flex', gap: SPACING.sm }}>
          <input type="date" value={dateStr} onChange={e => setDateStr(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
          <input type="time" value={timeStr} onChange={e => setTimeStr(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
          <input type="time" value={endTimeStr} onChange={e => setEndTimeStr(e.target.value)} style={{ ...inputStyle, flex: 1 }} />
        </div>
        <input type="text" value={tagsRaw} onChange={e => setTagsRaw(e.target.value)} placeholder="tags: punk, art, all-ages (comma-separated)" style={inputStyle} />

        <div style={{ display: 'flex', gap: SPACING.sm, alignItems: 'center' }}>
          <input ref={fileRef} type="file" accept="image/*,video/*" style={{ display: 'none' }} onChange={handleFlyerAttach} />
          <button
            onClick={() => fileRef.current?.click()}
            style={{
              background: 'none', border: `1px solid ${COLORS.border}`,
              color: COLORS.textMuted, padding: `${SPACING.xs} ${SPACING.md}`,
              cursor: 'pointer', fontSize: '11px', fontFamily: '"IBM Plex Mono", monospace',
            }}
          >
            + flyer
          </button>
          {flyerMagnetURI && (
            <span style={{ fontSize: '11px', color: COLORS.highlight }}>✓ {flyerType} attached</span>
          )}
          <button
            onClick={() => setOpen(false)}
            style={{ background: 'none', border: 'none', color: COLORS.textMuted, cursor: 'pointer', fontSize: '11px', fontFamily: '"IBM Plex Mono", monospace', padding: 0 }}
          >
            cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={posting || !title.trim() || !dateStr || !timeStr}
            style={{
              marginLeft: 'auto', background: COLORS.accent, color: '#fff',
              border: 'none', padding: `${SPACING.sm} ${SPACING.lg}`,
              cursor: posting || !title.trim() ? 'not-allowed' : 'pointer',
              fontSize: '13px', fontFamily: '"IBM Plex Mono", monospace',
              opacity: posting || !title.trim() ? 0.5 : 1,
            }}
          >
            {posting ? '…' : 'list event'}
          </button>
        </div>
        {error && <div style={{ fontSize: '11px', color: COLORS.danger }}>{error}</div>}
      </div>
    </div>
  );
}
